import { db, auth } from '@/firebase/config'
import {
  doc,
  setDoc,
  getDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  where,
  updateDoc,
  arrayUnion,
  deleteDoc
} from 'firebase/firestore'
import { trackBuyingJourney } from './customerJourneyService'
import { invalidateLeaderboardQueries } from '@/utils/invalidateLeaderboardQueries'
import {
  JOURNEY_FUNNEL_STAGES,
  JOURNEY_FUNNEL_EVENTS
} from '@/constants/buyingJourney'
import {
  applyEndToAttempts,
  applyEndToUnfinishedAttempts,
  buildAttemptStartData,
  mapAttemptForAnalytics
} from '@/utils/missionAttemptSession'

export const saveMissionProgress = async (missionData) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  const docId = `${user.uid}_${missionData.missionId}`
  const studentMissionRef = doc(db, 'studentMissionsProgress', docId)

  const newStudentMission = {
    ...missionData,
    userId: user.uid,
    updatedAt: serverTimestamp()
  }

  const docSnap = await getDoc(studentMissionRef)
  const existingData = docSnap.exists() ? docSnap.data() : null
  const wasComplete = existingData?.isComplete === true
  const isNowComplete = missionData.isComplete === true
  const justCompleted = !wasComplete && isNowComplete

  if (!docSnap.exists()) {
    newStudentMission.createdAt = serverTimestamp()
  }

  await setDoc(studentMissionRef, newStudentMission, { merge: true })

  if (justCompleted && missionData.missionId) {
    try {
      invalidateLeaderboardQueries()
    } catch {
      void 0
    }
    try {
      trackBuyingJourney({
        stage: JOURNEY_FUNNEL_STAGES.J_RETENTION,
        eventName: JOURNEY_FUNNEL_EVENTS.MISSION_COMPLETE,
        userId: user.uid,
        missionId: missionData.missionId,
        missionTitle: missionData.missionTitle || missionData.missionName
      })
      const completedQuery = query(
        collection(db, 'studentMissionsProgress'),
        where('userId', '==', user.uid),
        where('isComplete', '==', true)
      )
      const completedSnap = await getDocs(completedQuery)
      if (completedSnap.size === 1) {
        trackBuyingJourney({
          stage: JOURNEY_FUNNEL_STAGES.H_ACTIVATION,
          eventName: JOURNEY_FUNNEL_EVENTS.ACTIVATION_AHA,
          userId: user.uid,
          missionId: missionData.missionId,
          first_mission_complete: true
        })
      }
    } catch {
      void 0
    }
  }
}

/**
 * Track when a user starts a mission attempt
 * @param {string} missionId - The mission ID
 * @param {string} missionTitle - The mission title
 */
export const startMissionAttempt = async (missionId, missionTitle) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  try {
    const docId = `${user.uid}_${missionId}`
    const studentMissionRef = doc(db, 'studentMissionsProgress', docId)
    const docSnap = await getDoc(studentMissionRef)

    const attemptData = buildAttemptStartData()

    if (docSnap.exists()) {
      const existingData = docSnap.data()
      const currentAttemptCount = existingData.attemptCount || 0

      // Update existing document
      await updateDoc(studentMissionRef, {
        missionTitle: missionTitle,
        attemptCount: currentAttemptCount + 1,
        attempts: arrayUnion(attemptData),
        lastAttemptStartTime: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    } else {
      // Create new document
      const newDoc = {
        missionId: missionId,
        missionTitle: missionTitle,
        userId: user.uid,
        step: 'intro_scene',
        progress: 0,
        attemptCount: 1,
        attempts: [attemptData],
        lastAttemptStartTime: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      await setDoc(studentMissionRef, newDoc)
    }

    return attemptData.startTime
  } catch (error) {
    console.error('Error starting mission attempt:', error)
    throw error
  }
}

/**
 * Track when a user ends a mission attempt
 * @param {string} missionId - The mission ID
 * @param {Date} startTime - The start time of the session
 */
export const endMissionAttempt = async (missionId, startTime, endMeta = {}) => {
  const user = auth.currentUser
  if (!user || !startTime) {
    return { ok: false, skipped: true }
  }

  try {
    const docId = `${user.uid}_${missionId}`
    const studentMissionRef = doc(db, 'studentMissionsProgress', docId)
    const docSnap = await getDoc(studentMissionRef)

    if (!docSnap.exists()) {
      return { ok: true, skipped: true }
    }

    const data = docSnap.data()
    const result = applyEndToAttempts(data.attempts || [], startTime, endMeta)

    if (!result.changed) {
      return { ok: true, skipped: true }
    }

    await updateDoc(studentMissionRef, {
      attempts: result.attempts,
      totalTimeSpent: result.totalTimeSpent,
      lastSessionDuration: result.duration,
      updatedAt: serverTimestamp()
    })

    return { ok: true }
  } catch (error) {
    console.error('Error ending mission attempt:', error)
    return { ok: false, error }
  }
}

export const finalizeUnfinishedMissionAttempts = async (
  missionId,
  endMeta = {}
) => {
  const user = auth.currentUser
  if (!user || !missionId) {
    return { ok: false, skipped: true }
  }

  try {
    const docId = `${user.uid}_${missionId}`
    const studentMissionRef = doc(db, 'studentMissionsProgress', docId)
    const docSnap = await getDoc(studentMissionRef)

    if (!docSnap.exists()) {
      return { ok: true, skipped: true }
    }

    const data = docSnap.data()
    const result = applyEndToUnfinishedAttempts(data.attempts || [], endMeta)

    if (!result.changed) {
      return { ok: true, skipped: true }
    }

    await updateDoc(studentMissionRef, {
      attempts: result.attempts,
      totalTimeSpent: result.totalTimeSpent,
      lastSessionDuration: result.duration,
      updatedAt: serverTimestamp()
    })

    return { ok: true }
  } catch (error) {
    console.error('Error finalizing unfinished mission attempts:', error)
    return { ok: false, error }
  }
}

/**
 * Get mission analytics data
 * @param {string} missionId - The mission ID
 */
export const getMissionAnalytics = async (missionId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  try {
    const docId = `${user.uid}_${missionId}`
    const studentMissionRef = doc(db, 'studentMissionsProgress', docId)
    const docSnap = await getDoc(studentMissionRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      const attempts = data.attempts || []

      // Calculate analytics
      const completedAttempts = attempts.filter(
        (attempt) => attempt.endTime
      ).length
      const totalDuration = attempts.reduce(
        (total, attempt) => total + (attempt.duration || 0),
        0
      )
      const averageDuration =
        completedAttempts > 0 ? totalDuration / completedAttempts : 0

      return {
        attemptCount: data.attemptCount || 0,
        completedAttempts,
        totalTimeSpent: totalDuration,
        averageSessionDuration: averageDuration,
        attempts: attempts.map((attempt) => mapAttemptForAnalytics(attempt)),
        lastAttempt: data.lastAttemptStartTime?.toDate?.() || null
      }
    }

    return {
      attemptCount: 0,
      completedAttempts: 0,
      totalTimeSpent: 0,
      averageSessionDuration: 0,
      attempts: [],
      lastAttempt: null
    }
  } catch (error) {
    console.error('Error fetching mission analytics:', error)
    throw error
  }
}

export const getMissionProgress = async (missionId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  try {
    const docId = `${user.uid}_${missionId}`
    const studentMissionRef = doc(db, 'studentMissionsProgress', docId)
    const docSnap = await getDoc(studentMissionRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      return data
    } else {
      // Return default progress if no document exists
      return {
        missionId: missionId,
        userId: user.uid,
        step: 'intro_scene',
        progress: 0,
        attemptCount: 0,
        totalTimeSpent: 0,
        attempts: []
      }
    }
  } catch (error) {
    console.error('Error fetching mission progress:', error)
    throw error
  }
}

export const getStudentMissionsProgress = async () => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  try {
    const studentMissionsRef = collection(db, 'studentMissionsProgress')
    const q = query(studentMissionsRef, where('userId', '==', user.uid))
    const docSnap = await getDocs(q)
    const missionsProgress = docSnap.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        ...data,
        analytics: {
          attemptCount: data.attemptCount || 0,
          totalTimeSpent: data.totalTimeSpent || 0,
          lastAttempt: data.lastAttemptStartTime?.toDate?.() || null,
          averageSessionDuration:
            data.attempts?.length > 0
              ? (data.totalTimeSpent || 0) / data.attempts.length
              : 0
        }
      }
    })

    return missionsProgress
  } catch (error) {
    console.error('Error fetching student missions progress:', error)
    throw error
  }
}

/**
 * Reset mission progress to 0 and step to intro_scene without deleting the document
 * This preserves analytics, attemptCount, attempts, and other metadata
 * @param {string|number} missionId - The mission ID
 */
export const resetMissionProgress = async (missionId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  try {
    const docId = `${user.uid}_${missionId}`
    const studentMissionRef = doc(db, 'studentMissionsProgress', docId)
    const docSnap = await getDoc(studentMissionRef)

    if (docSnap.exists()) {
      await updateDoc(studentMissionRef, {
        progress: 0,
        step: 'intro_scene',
        updatedAt: serverTimestamp()
      })
    } else {
      // If document doesn't exist, create it with default values
      await setDoc(studentMissionRef, {
        missionId: missionId,
        userId: user.uid,
        step: 'intro_scene',
        progress: 0,
        isComplete: false,
        attemptCount: 0,
        totalTimeSpent: 0,
        attempts: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }
    return true
  } catch (error) {
    console.error('Error resetting mission progress:', error)
    throw error
  }
}

/**
 * Update instruction video watched status
 * @param {string|number} missionId - The mission ID
 */
export const updateInstructionVideoWatched = async (missionId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  try {
    const docId = `${user.uid}_${missionId}`
    const studentMissionRef = doc(db, 'studentMissionsProgress', docId)

    await setDoc(
      studentMissionRef,
      {
        hasWatched: true,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    )

    return true
  } catch (error) {
    console.error('Error updating instruction video status:', error)
    throw error
  }
}

/**
 * Delete mission progress for a specific mission
 * @param {string|number} missionId - The mission ID
 */
export const deleteMissionProgress = async (missionId) => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }

  try {
    const docId = `${user.uid}_${missionId}`
    const studentMissionRef = doc(db, 'studentMissionsProgress', docId)
    await deleteDoc(studentMissionRef)
    return true
  } catch (error) {
    console.error('Error deleting mission progress:', error)
    throw error
  }
}
